import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useQuote } from '../context/QuoteContext';
import { Menu, X, Globe, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navigation = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { getItemCount } = useQuote();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();
  const quoteCount = getItemCount();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      return () => {
        document.documentElement.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes, settingsRes] = await Promise.all([
          axios.get(`${API}/categories/tree`),
          axios.get(`${API}/products`),
          axios.get(`${API}/settings`)
        ]);
        setCategories(catRes.data);
        setProducts(prodRes.data);
        setSiteSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowProductsDropdown(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowProductsDropdown(false);
    }, 150);
  };

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/produkter', label: t('nav.products'), hasDropdown: true },
    { href: '/dimensionering', label: 'Dimensionering' },
    { href: '/kunskap', label: language === 'sv' ? 'Kunskap' : 'Knowledge' },
    { href: '/jobb', label: t('nav.jobs') },
    { href: '/bibliotek', label: t('nav.library') },
    { href: '/om-oss', label: t('nav.about') },
    { href: '/kontakt', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  // Group products by category - now using hierarchical structure
  const productsByCategory = categories.map(cat => ({
    ...cat,
    products: products.filter(p => p.main_category_id === cat.id).slice(0, 3),
    children: cat.children?.map(subCat => ({
      ...subCat,
      products: products.filter(p => p.sub_category_id === subCat.id).slice(0, 3)
    })) || []
  }));

  const CATEGORY_MENU_IMAGES = {
    'aterstromning': 'https://customer-assets-eiarnc6j.emergentagent.net/job_4a968155-7326-4340-bab2-a87725307ac2/artifacts/8mmb5l34_atorstromning-menu.png',
    'expansionssystem': 'https://customer-assets-eiarnc6j.emergentagent.net/job_4a968155-7326-4340-bab2-a87725307ac2/artifacts/gpgk77nt_expansion-menu.png',
    'matare': 'https://customer-assets-eiarnc6j.emergentagent.net/job_4a968155-7326-4340-bab2-a87725307ac2/artifacts/kywv78ot_matare-menu.png',
    'luft-partikelavskiljare': 'https://customer-assets-eiarnc6j.emergentagent.net/job_4a968155-7326-4340-bab2-a87725307ac2/artifacts/6hgygj46_luftpartikel-menu.png',
    'varmevaxlare': 'https://customer-assets-eiarnc6j.emergentagent.net/job_4a968155-7326-4340-bab2-a87725307ac2/artifacts/evj85q0s_VVX-menu.png',
    'automatisering-manovrering': 'https://customer-assets-eiarnc6j.emergentagent.net/job_4a968155-7326-4340-bab2-a87725307ac2/artifacts/gktseo5l_automation-menu.png',
    'pumpar': 'https://customer-assets-eiarnc6j.emergentagent.net/job_4a968155-7326-4340-bab2-a87725307ac2/artifacts/aceik6m2_pumpar-menu.png',
    'ventiler': 'https://customer-assets-eiarnc6j.emergentagent.net/job_4a968155-7326-4340-bab2-a87725307ac2/artifacts/kqwikncs_ventiler-menu.png',
    'tankar': 'https://customer-assets-eiarnc6j.emergentagent.net/job_4a968155-7326-4340-bab2-a87725307ac2/artifacts/4273ms6o_Ackumulator-menu.png',
  };

  // Swap Mätare and Ventiler in the dropdown so tall columns group together (fits without scroll)
  const iMatare = productsByCategory.findIndex(c => c.slug === 'matare');
  const iVentiler = productsByCategory.findIndex(c => c.slug === 'ventiler');
  if (iMatare > -1 && iVentiler > -1) {
    [productsByCategory[iMatare], productsByCategory[iVentiler]] = [productsByCategory[iVentiler], productsByCategory[iMatare]];
  }

  // Check if we're on the homepage for transparent nav effect
  const isHomePage = location.pathname === '/';
  const useTransparentNav = isHomePage && !scrolled && !isOpen;

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        !useTransparentNav
          ? 'bg-white shadow-sm border-b border-stone-200' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-2 shrink-0"
            data-testid="nav-logo"
          >
            {siteSettings?.logo_url ? (
              <img 
                src={siteSettings.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${siteSettings.logo_url}` : siteSettings.logo_url}
                alt={siteSettings.company_name || 'Flowingo'}
                onError={(e) => { if (!e.target.src.endsWith('/flowingo-logo.svg')) e.target.src = '/flowingo-logo.svg'; }}
                className={`h-12 sm:h-[50px] max-w-[240px] object-contain transition-all duration-500 ${useTransparentNav ? 'brightness-0 invert' : ''}`}
              />
            ) : (
              <img
                src="/flowingo-logo.svg"
                alt="Flowingo"
                className={`h-12 sm:h-[50px] max-w-[240px] object-contain transition-all duration-500 ${useTransparentNav ? 'brightness-0 invert' : ''}`}
              />
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div
                  key={link.href}
                  className="relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  ref={dropdownRef}
                >
                  <Link
                    to={link.href}
                    data-testid="nav-link-products"
                    className={`px-4 py-2 text-sm font-medium transition-all duration-500 rounded-md flex items-center gap-1 ${
                      isActive(link.href) || location.pathname.startsWith('/products')
                        ? useTransparentNav ? 'text-white bg-white/20' : 'text-stone-900 bg-stone-100'
                        : useTransparentNav ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                    }`}
                  >
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showProductsDropdown ? 'rotate-180' : ''}`} />
                  </Link>

                  {/* Mega Dropdown */}
                  {showProductsDropdown && (
                    <div 
                      className="absolute top-full left-0 xl:-left-80 mt-1 bg-white border border-stone-200 shadow-xl min-w-[800px] xl:min-w-[1280px] max-w-[calc(100vw-3rem)] max-h-[calc(100vh-7rem)] overflow-y-auto z-50"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="p-8">
                        <div className="grid grid-cols-4 xl:grid-cols-5 gap-x-8 gap-y-10">
                          {productsByCategory.map(cat => (
                            <div key={cat.id} className="flex items-start gap-3">
                              {CATEGORY_MENU_IMAGES[cat.slug] && (
                                <img
                                  src={CATEGORY_MENU_IMAGES[cat.slug]}
                                  alt={language === 'sv' ? cat.name_sv : cat.name_en}
                                  data-testid={`menu-illustration-${cat.slug}`}
                                  className="w-[70px] h-[70px] object-contain shrink-0 mt-0.5"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                              {/* Main Category Header */}
                              <Link 
                                to={`/produkter/kategori/${cat.slug}`}
                                className="font-semibold text-stone-900 hover:text-[#321a27] flex items-center gap-1 mb-2 pb-2 border-b border-stone-200 text-sm"
                                onClick={() => setShowProductsDropdown(false)}
                              >
                                {language === 'sv' ? cat.name_sv : cat.name_en}
                                <ChevronRight className="w-3 h-3" />
                              </Link>
                              
                              {/* Subcategories only - no products */}
                              {cat.children?.length > 0 && (
                                <ul className="space-y-1.5">
                                  {cat.children.map(subCat => (
                                    <li key={subCat.id}>
                                      <Link
                                        to={`/produkter/kategori/${cat.slug}/${subCat.slug}`}
                                        className="text-xs text-stone-600 hover:text-[#321a27] block leading-snug break-words pr-1"
                                        onClick={() => setShowProductsDropdown(false)}
                                      >
                                        {language === 'sv' ? subCat.name_sv : subCat.name_en}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Footer link */}
                        <div className="mt-4 pt-3 border-t border-stone-200">
                          <Link 
                            to="/produkter"
                            className="text-sm font-medium text-[#321a27] hover:underline"
                            onClick={() => setShowProductsDropdown(false)}
                          >
                            {language === 'sv' ? 'Visa alla produkter →' : 'View all products →'}
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-500 rounded-md ${
                    isActive(link.href)
                      ? useTransparentNav ? 'text-white bg-white/20' : 'text-stone-900 bg-stone-100'
                      : useTransparentNav ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Quick quote CTA */}
            <Link
              to="/snabboffert"
              data-testid="header-quick-quote-cta"
              className={`px-4 py-2 text-sm font-semibold transition-colors duration-300 ${useTransparentNav ? 'bg-white text-[#321a27] hover:bg-stone-100' : 'bg-[#321a27] text-white hover:bg-[#4a2a3a]'}`}
            >
              {language === 'sv' ? 'Snabboffert' : 'Quick quote'}
            </Link>

            {/* Quote List */}
            <Link to="/offert" className={`relative p-2 transition-colors duration-500 ${useTransparentNav ? 'text-white/90 hover:text-white' : 'text-stone-600 hover:text-stone-900'}`} data-testid="quote-list-icon">
              <FileText className="w-5 h-5" />
              {quoteCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#321a27] text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {quoteCount}
                </span>
              )}
            </Link>
            
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className={`transition-colors duration-500 ${useTransparentNav ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-100'}`}
            >
              <Globe className="w-4 h-4 mr-2" />
              {language === 'sv' ? 'EN' : 'SV'}
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/offert" className={`relative p-2 transition-colors duration-500 ${useTransparentNav ? 'text-white/90 hover:text-white' : 'text-stone-600 hover:text-stone-900'}`}>
              <FileText className="w-5 h-5" />
              {quoteCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#321a27] text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {quoteCount}
                </span>
              )}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className={`transition-colors duration-500 ${useTransparentNav ? 'text-white/90 hover:text-white' : 'text-stone-600 hover:text-stone-900'}`}
            >
              <Globe className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
              className={`transition-colors duration-500 ${useTransparentNav ? 'text-white/90 hover:text-white' : 'text-stone-600 hover:text-stone-900'}`}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-stone-200 max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <div key={link.href}>
                <Link
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 text-base font-medium rounded-lg ${
                    isActive(link.href)
                      ? 'text-stone-900 bg-stone-100'
                      : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                  }`}
                >
                  {link.label}
                </Link>
                {/* Mobile category submenu */}
                {link.hasDropdown && (
                  <div className="ml-4 mt-1 space-y-1">
                    {categories.map(cat => (
                      <Link
                        key={cat.id}
                        to={`/produkter/kategori/${cat.slug}`}
                        onClick={() => setIsOpen(false)}
                        data-testid={`mobile-category-link-${cat.slug}`}
                        className="block px-4 py-2 text-sm text-stone-500 hover:text-stone-900"
                      >
                        {language === 'sv' ? cat.name_sv : cat.name_en}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>

  );
};

export default Navigation;
